/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useRef, useState } from "react";

import { makePost } from "@culturehq/client";
import { Button, classnames, Icon, Loader, ModalDialog, PlainButton } from "@culturehq/components";

import { useDoor, useNotificationReceivedListener } from "app/hooks";
import { getDuration, sleep, Trans } from "app/utils";
import VideoTrim from "components/common/VideoTrim";
import notificationsTypes from "constants/notificationsTypes";
import ProgressLine from "components/admin/analytics/ProgressLine";
import ChqModalDialog from "components/common/ChqModalDialog";
import PlaybackGrabber from "./PlaybackGrabber";

const TrimVideo = ({
  initialOpen = false,
  src,
  story,
  storyQuestion,
  thumbnail,
  onVideoTrimmed,
  fillParent,
  handleOpen
}) => {
  const videoRef = useRef();
  const cursorMarkerRef = useRef();
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [trimVideo, setTrimVideo] = useState(false);
  const [playerStart, setPlayerStart] = useState(0);
  const [playerEnd, setPlayerEnd] = useState();
  const [loading, setLoading] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(1);
  const [videoTime, setVideoTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [seekValue, setSeekValue] = useState(0);
  const [progress, setProgress] = useState(0);

  const playerStartRef = useRef();
  const playerEndRef = useRef();
  const progressRef = useRef();
  playerStartRef.current = playerStart;
  playerEndRef.current = playerEnd;
  progressRef.current = progress;

  const { open, onOpen: onOpenModal, onClose } = useDoor(initialOpen);

  const handleVideoTrimmed = useCallback((video, editingVideo = false) => {
    setLoading(false);
    setTrimVideo(false);
    onVideoTrimmed(video, editingVideo);
    onClose();
  }, [onClose, onVideoTrimmed]
  );

  const onOpen = () => {
    if (handleOpen) {
      handleOpen();
    }

    onOpenModal();
  };

  useNotificationReceivedListener(
    useCallback(
      notification => {
        switch (notification.notificationType) {
          case notificationsTypes.TRIM_VIDEO_ERROR:
            if (story?.id === notification.entity.id
              || storyQuestion?.id === notification.entity.id) {
              setLoading(false);
              setProgress(0);
            }
            break;
          case notificationsTypes.TRIM_VIDEO_DONE:
            if (story?.id === notification.entity.id
              || storyQuestion?.id === notification.entity.id) {
              handleVideoTrimmed(notification.entity.media.draftUrl, true);
              setProgress(0);
            }
            break;
          default:
            break;
        }
      },
      [handleVideoTrimmed, story, storyQuestion]
    )
  );

  const handleVideoClick = () => {
    if (Number.isFinite(videoTime)) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
        if (videoRef.current.currentTime >= playerEnd) {
          handleVideoRefUpdateTime(playerStart);
        }
      }
    }
  };

  const pauseVideo = () => {
    videoRef.current.pause();
  };

  const setProgressBarAdvance = async () => {
    // Multiply per 10 because we put the sleep in 100, so we are using milliseconds, not seconds
    const renderTime = (videoTime / 60) * 60 * 10;
    /* eslint-disable no-await-in-loop */
    for (let i = 0; i < renderTime; i += 1) {
      await sleep(100);
      setProgress(progressRef.current + (90 / renderTime));
    }
    /* eslint-enable no-await-in-loop */
  };

  const handleTrim = async () => {
    setLoading(true);
    setProgress(1);
    if (src.startsWith("https://culturehq-direct-uploads")) {
      await makePost("/video/trim",
        {
          url: src,
          start: playerStart,
          end: playerEnd,
          storyId: story?.id,
          storyQuestionId: storyQuestion?.id
        }
      );
    } else {
      setTrimVideo(true);
    }
    await setProgressBarAdvance();
  };

  const changeVolume = () => {
    if (videoRef.current.volume) {
      setVolumeLevel(0);
      videoRef.current.volume = 0;
    } else {
      setVolumeLevel(1);
      videoRef.current.volume = 1;
    }
  };

  const handleCursorMarkerRefUpdateDisplay = display => {
    cursorMarkerRef.current.style.display = display;
  };

  const handleCursorMarkerRefUpdateLeft = value => {
    cursorMarkerRef.current.style.left = value;
  };

  const handleVideoRefUpdateTime = value => {
    videoRef.current.currentTime = value;
  };

  return (
    <>
      {open && (
        <ChqModalDialog entrance="zoomIn" onClose={onClose} className="trim-video-modal" ariaLabelledBy="trim-video-title" ariaDescribedby="trim-video-title" width="normal">
          <ModalDialog.Heading bigCloseIcon onClose={onClose}>
            <span><h3 className="trim-video-title" id="trim-video-title"><Trans>Trim Video</Trans></h3></span>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="gallery-lightbox__photo">
              <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${thumbnail})` }} />
              <div className={classnames("Video-Container", { "Video-Container__Paused": !videoPlaying })}>
                <video
                  ref={videoRef}
                  autoPlay
                  className="Video-Container__Video"
                  onPause={() => setVideoPlaying(false)}
                  onPlay={() => setVideoPlaying(true)}
                  poster={thumbnail}
                  src={src}
                  onTimeUpdate={e => {
                    if (Number.isFinite(videoTime)) {
                      handleCursorMarkerRefUpdateLeft(`${(e.target.currentTime / videoRef.current.duration) * 100}%`);
                    }
                    setSeekValue((e.target.currentTime * 100) / videoRef.current.duration);
                    const current = e.target.currentTime - playerStartRef.current;
                    setCurrentTime(current < 0 || current > videoTime ? 0 : current);
                    if (videoTime === Infinity && Number.isFinite(videoRef.current.duration)) {
                      setVideoTime(videoRef.current.duration);
                      setPlayerEnd(videoRef.current.duration);
                    }
                    if (videoRef.current.currentTime >= playerEnd) {
                      pauseVideo();
                    }
                  }}
                  onLoadedData={() => {
                    setVideoTime(videoRef.current.duration);
                    setPlayerEnd(videoRef.current.duration);
                  }}
                  onClick={handleVideoClick}
                />
                <div className="Video--Controls">
                  <div className="Video--Controls_Container">
                    <div className="Video--Controls-Play">
                      {videoPlaying ? (
                        <PlainButton
                          className="Btn-Play"
                          onClick={handleVideoClick}
                        >
                          <Icon icon="pause-no-bg" />
                        </PlainButton>
                      ) : (
                        <PlainButton
                          className="Btn-Play"
                          onClick={handleVideoClick}
                        >
                          <Icon icon="play-no-border" />
                        </PlainButton>
                      )}
                      <span className="Video-Duration">{getDuration(currentTime)}/{getDuration(videoTime)}</span>
                    </div>
                    <div className="Video--Controls-Utilities">
                      <PlainButton
                        className="Volume-Btn"
                        onClick={changeVolume}
                      >
                        {volumeLevel === 0 ? <Icon icon="speaker-muted" /> : <Icon icon="speaker" />}
                      </PlainButton>
                    </div>
                  </div>
                  <input
                    className="Seek-Bar"
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={seekValue}
                    onChange={e => {
                      const seekto = videoRef.current.duration * (+e.target.value / 100);
                      handleVideoRefUpdateTime(seekto);
                      setSeekValue(e.target.value);
                      const current = seekto - playerStartRef.current;
                      setCurrentTime(current < 0 || current > videoTime ? 0 : current);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="playback-ref">
              <PlaybackGrabber
                cursorMarkerRef={cursorMarkerRef}
                onCursorMarkerRefUpdateDisplay={handleCursorMarkerRefUpdateDisplay}
                onCursorMarkerRefUpdateLeft={handleCursorMarkerRefUpdateLeft}
                onVideoRefUpdateTime={handleVideoRefUpdateTime}
                playerEndRef={playerEndRef}
                playerStartRef={playerStartRef}
                setPlayerEnd={setPlayerEnd}
                setPlayerStart={setPlayerStart}
                setVideoTime={setVideoTime}
                thumbnail={thumbnail}
                videoRef={videoRef}
                videoTime={videoTime}
              />
            </div>
            {trimVideo && (
              <VideoTrim
                video={src}
                onFinish={handleVideoTrimmed}
                onProgress={() => {}}
                start={playerStart}
                end={playerEnd}
              />
            )}
            {loading && (
              <>
                {progress !== 0 && (
                  <ProgressLine
                    className="story-progressbar"
                    color="#79b17d"
                    valueNow={progress}
                    valueMax={100}
                  />
                )}
                <Loader loading />
              </>
            )}
          </ModalDialog.Body>
          <ModalDialog.Footer>
            {!Number.isFinite(videoTime) && (
              <span>
                <Trans>Once your video is done processing, you will be able to trim</Trans>
              </span>
            )}
            <Button disabled={!Number.isFinite(videoTime)} onClick={handleTrim}>
              <Trans>Trim</Trans>
            </Button>
          </ModalDialog.Footer>
        </ChqModalDialog>
      )}
      {fillParent ? (
        <div className="TrimVideo-Container" onClick={onOpen}>
          <PlainButton className="TrimVideo-Btn" onClick={onOpen}>
            <Icon icon="scissors" />
            <Trans>Trim video</Trans>
          </PlainButton>
        </div>
      ) : (
        <Button className="TrimVideo-Btn" onClick={onOpen}>
          <Icon icon="scissors" />
        </Button>
      )}
    </>
  );
};

export default TrimVideo;
